import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AG_GRID_LOCALE_BR } from '@ag-grid-community/locale';
import type {
  ColDef,
  GridReadyEvent,
  CellValueChangedEvent,
  GetRowIdParams,
} from 'ag-grid-community';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import { useIsMobile } from '../../hooks/useIsMobile';

const themeMobile = themeQuartz.withParams({
  rowHeight: 32,
  headerHeight: 32,
  fontSize: 12,
  cellHorizontalPadding: 6,
  spacing: 2,
});
import type { RowClassParams } from 'ag-grid-community';
import {
  MagnifyingGlassIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  FunnelIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import type { Agrupamento, ProdutoManutencao, ManutencaoLoteItem } from '../../api/agrupamento';
import { agrupamentoService } from '../../api/agrupamento';
import { pesquisasService } from '../../api/pesquisas';
import type { PesquisaMarca } from '../../types/pesquisas';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Select estilizado ─────────────────────────────────────────────────────────

const SelectField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ value, onChange, disabled, className = '', children }) => (
  <div className={`relative inline-flex items-center ${className}`}>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className="w-full appearance-none bg-white dark:bg-zinc-700 text-sm text-zinc-900 dark:text-zinc-100 border border-zinc-300 dark:border-zinc-600 rounded-lg pl-3 pr-8 py-1.5 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:border-zinc-400 dark:hover:border-zinc-500 cursor-pointer"
    >
      {children}
    </select>
    <ChevronDownIcon className="pointer-events-none absolute right-2 w-3.5 h-3.5 text-zinc-400 flex-none" />
  </div>
);

// ── Row type ──────────────────────────────────────────────────────────────────

export interface ProdutoManutencaoRow extends ProdutoManutencao {
  grupoId: number | null;
  grupoDescricao: string;
  nomeEmbalagem: string;
  visivelCat: string;
  dataAtualizacao: string | null;
  horaAtualizacao: string | null;
  // rastreamento de mudanças
  _grupoIdOriginal: number | null;
  _nomeEmbalagemOriginal: string;
  _visivelCatOriginal: string;
  _modificado: boolean;
}

// ── Cell renderer: Agrupamento (select inline) ───────────────────────────────

const AgrupamentoCellRenderer: React.FC<CustomCellRendererProps> = ({ data, context }) => {
  const agrupamentos: Agrupamento[] = context?.agrupamentos ?? [];
  const onGrupoChange: (codigo: string, novoGrupoId: number | null) => void =
    context?.onGrupoChange ?? (() => {});

  const semGrupo = data?.grupoId == null || data?.grupoId === '';

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', width: '100%', padding: '0 2px' }}>
      <select
        value={data?.grupoId ?? ''}
        onChange={e => {
          const val = e.target.value === '' ? null : Number(e.target.value);
          onGrupoChange(data?.codigo, val);
        }}
        style={{
          width: '100%',
          height: 26,
          fontSize: 13,
          paddingLeft: 6,
          paddingRight: 22,
          border: 'none',
          borderRadius: 4,
          background: semGrupo ? 'transparent' : 'rgba(59,130,246,0.08)',
          cursor: 'pointer',
          outline: 'none',
          appearance: 'none',
          WebkitAppearance: 'none',
          color: semGrupo ? '#94a3b8' : 'inherit',
          fontStyle: semGrupo ? 'italic' : 'normal',
        }}
      >
        <option value="">— Sem grupo —</option>
        {agrupamentos.map((ag: Agrupamento) => (
          <option key={ag.grupoId} value={ag.grupoId}>
            {ag.descricao}
          </option>
        ))}
      </select>
      {/* Chevron personalizado */}
      <svg
        style={{ position: 'absolute', right: 5, pointerEvents: 'none', color: '#94a3b8', flexShrink: 0 }}
        width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────

interface Props {
  agrupamentos: Agrupamento[];
  onCriarAgrupamento: () => void;
  onAgrupamentosChange: () => void;
}

export const ManutencaoProdutosGrid: React.FC<Props> = ({
  agrupamentos,
  onCriarAgrupamento,
  onAgrupamentosChange,
}) => {
  const isMobile = useIsMobile();
  const gridRef = useRef<AgGridReact<ProdutoManutencaoRow>>(null);
  const [marcas, setMarcas] = useState<PesquisaMarca[]>([]);
  const [marcaSelecionada, setMarcaSelecionada] = useState('');
  const [marcasCarregando, setMarcasCarregando] = useState(false);
  const [termo, setTermo] = useState('');
  const [rowData, setRowData] = useState<ProdutoManutencaoRow[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [alteracoes, setAlteracoes] = useState(
    new Map<string, ProdutoManutencaoRow>(),
  );
  const [mensagem, setMensagem] = useState<{
    tipo: 'sucesso' | 'erro';
    texto: string;
  } | null>(null);

  const isDark = document.documentElement.classList.contains('dark');

  // ── State para "Aplicar a todos" ──────────────────────────────────────────────

  const [grupoParaTodos, setGrupoParaTodos] = useState<string>('');

  // ── Callback passado via context para o renderer inline de agrupamento ────────

  const onGrupoChange = useCallback((codigo: string, novoGrupoId: number | null) => {
    setRowData(prev => {
      const novasRows = prev.map(r => {
        if (r.codigo !== codigo) return r;
        const isModified =
          novoGrupoId !== r._grupoIdOriginal ||
          r.nomeEmbalagem !== r._nomeEmbalagemOriginal ||
          r.visivelCat !== r._visivelCatOriginal;
        return { ...r, grupoId: novoGrupoId, _modificado: isModified };
      });
      setAlteracoes(() => {
        const next = new Map<string, ProdutoManutencaoRow>();
        novasRows.forEach(r => { if (r._modificado) next.set(r.codigo, r); });
        return next;
      });
      return novasRows;
    });
  }, []);

  // ── Carregar marcas ao montar ─────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    setMarcasCarregando(true);
    pesquisasService.getMarcas()
      .then(data => {
        if (!cancelled) setMarcas(
          [...data].sort((a, b) => a.descricaomarca.localeCompare(b.descricaomarca, 'pt-BR'))
        );
      })
      .catch(() => {
        if (!cancelled) setMensagem({ tipo: 'erro', texto: 'Erro ao carregar marcas.' });
      })
      .finally(() => {
        if (!cancelled) setMarcasCarregando(false);
      });
    return () => { cancelled = true; };
  }, []);

  // ── Validação para habilitar busca ────────────────────────────────────────────

  const podeBuscar = marcaSelecionada !== '' && termo.trim().length >= 2;

  // ── Busca ────────────────────────────────────────────────────────────────────

  const buscar = useCallback(async () => {
    if (!podeBuscar) return;
    setCarregando(true);
    setAlteracoes(new Map());
    setMensagem(null);
    try {
      const produtos = await agrupamentoService.listarProdutosManutencao(
        marcaSelecionada,
        termo.trim(),
      );
      const rows: ProdutoManutencaoRow[] = produtos.map(p => ({
        ...p,
        grupoId: p.grupoId ?? null,
        grupoDescricao: p.grupoDescricao ?? '',
        nomeEmbalagem: p.nomeEmbalagem ?? '',
        visivelCat: p.visivelCat ?? 'S',
        dataAtualizacao: p.dataAtualizacao ?? null,
        horaAtualizacao: p.horaAtualizacao ?? null,
        _grupoIdOriginal: p.grupoId ?? null,
        _nomeEmbalagemOriginal: p.nomeEmbalagem ?? '',
        _visivelCatOriginal: p.visivelCat ?? 'S',
        _modificado: false,
      }));
      setRowData(rows);
      if (rows.length === 0)
        setMensagem({ tipo: 'erro', texto: 'Nenhum produto encontrado.' });
    } catch {
      setMensagem({ tipo: 'erro', texto: 'Erro ao buscar produtos.' });
    } finally {
      setCarregando(false);
    }
  }, [marcaSelecionada, termo, podeBuscar]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && podeBuscar) buscar();
  };

  // ── Mudança de célula ─────────────────────────────────────────────────────────

  // ── Mudança de célula (apenas nomeEmbalagem usa o mecanismo nativo do AG Grid) ──

  const handleCellValueChanged = useCallback(
    (e: CellValueChangedEvent<ProdutoManutencaoRow>) => {
      const row = e.data;
      if (!row) return;

      const isModified =
        row.grupoId !== row._grupoIdOriginal ||
        row.nomeEmbalagem !== row._nomeEmbalagemOriginal ||
        row.visivelCat !== row._visivelCatOriginal;

      const updatedRow: ProdutoManutencaoRow = { ...row, _modificado: isModified };

      e.node.setData(updatedRow);
      setRowData(prev => prev.map(r => r.codigo === updatedRow.codigo ? updatedRow : r));

      setAlteracoes(prev => {
        const next = new Map(prev);
        if (isModified) next.set(updatedRow.codigo, updatedRow);
        else next.delete(updatedRow.codigo);
        return next;
      });
    },
    [],
  );

  // ── Salvar ────────────────────────────────────────────────────────────────────

  const salvar = useCallback(async () => {
    if (alteracoes.size === 0) return;
    setSalvando(true);
    setMensagem(null);
    try {
      const items: ManutencaoLoteItem[] = Array.from(alteracoes.values()).map(
        row => ({
          codigo: row.codigo,
          grupoIdAnterior: row._grupoIdOriginal,
          grupoIdNovo: row.grupoId,
          nomeEmbalagem: row.nomeEmbalagem,
          visivelCat: row.visivelCat,
        }),
      );
      await agrupamentoService.salvarManutencaoLote(items);

      const now = new Date();
      const nowIso = now.toISOString();
      const nowHora = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;

      // Confirmar como novos valores originais
      setRowData(prev =>
        prev.map(r => {
          if (!alteracoes.has(r.codigo)) return r;
          return {
            ...r,
            _grupoIdOriginal: r.grupoId,
            _nomeEmbalagemOriginal: r.nomeEmbalagem,
            _visivelCatOriginal: r.visivelCat,
            dataAtualizacao: nowIso,
            horaAtualizacao: nowHora,
            _modificado: false,
          };
        }),
      );
      setAlteracoes(new Map());
      onAgrupamentosChange();
      setMensagem({
        tipo: 'sucesso',
        texto: `${items.length} alteração(ões) salva(s) com sucesso.`,
      });
      setTimeout(() => setMensagem(null), 4000);
    } catch {
      setMensagem({ tipo: 'erro', texto: 'Erro ao salvar. Tente novamente.' });
    } finally {
      setSalvando(false);
    }
  }, [alteracoes, onAgrupamentosChange]);

  // ── Descartar ─────────────────────────────────────────────────────────────────

  const descartar = useCallback(() => {
    setRowData(prev =>
      prev.map(r => {
        if (!r._modificado) return r;
        const ag = agrupamentos.find(a => a.grupoId === r._grupoIdOriginal);
        return {
          ...r,
          grupoId: r._grupoIdOriginal,
          grupoDescricao: ag?.descricao ?? '',
          nomeEmbalagem: r._nomeEmbalagemOriginal,
          visivelCat: r._visivelCatOriginal,
          _modificado: false,
        };
      }),
    );
    setAlteracoes(new Map());
  }, [agrupamentos]);

  // ── Toggle Visível Catálogo ───────────────────────────────────────────────

  const toggleVisivelCat = useCallback((codigo: string) => {
    setRowData(prev => {
      const novasRows = prev.map(r => {
        if (r.codigo !== codigo) return r;
        const novoValor = r.visivelCat === 'S' ? 'N' : 'S';
        const isModified =
          novoValor !== r._visivelCatOriginal ||
          r.grupoId !== r._grupoIdOriginal ||
          r.nomeEmbalagem !== r._nomeEmbalagemOriginal;
        return { ...r, visivelCat: novoValor, _modificado: isModified };
      });

      // Sincronizar mapa de alterações
      setAlteracoes(() => {
        const next = new Map<string, ProdutoManutencaoRow>();
        novasRows.forEach(r => { if (r._modificado) next.set(r.codigo, r); });
        return next;
      });

      return novasRows;
    });
  }, []);

  // ── Aplicar agrupamento a todos ───────────────────────────────────────────────

  const aplicarATodos = useCallback(() => {
    if (rowData.length === 0) return;
    const novoGrupoId = grupoParaTodos === '' ? null : Number(grupoParaTodos);
    const ag = agrupamentos.find(a => a.grupoId === novoGrupoId);
    const novaDescricao = ag?.descricao ?? '';

    const nextAlteracoes = new Map<string, ProdutoManutencaoRow>();

    const novasRows = rowData.map(r => {
      const isModified =
        novoGrupoId !== r._grupoIdOriginal ||
        r.nomeEmbalagem !== r._nomeEmbalagemOriginal;

      const updated: ProdutoManutencaoRow = {
        ...r,
        grupoId: novoGrupoId,
        grupoDescricao: novaDescricao,
        _modificado: isModified,
      };

      if (isModified) {
        nextAlteracoes.set(updated.codigo, updated);
      }

      return updated;
    });

    setRowData(novasRows);
    setAlteracoes(nextAlteracoes);
  }, [rowData, grupoParaTodos, agrupamentos]);

  // ── Colunas ───────────────────────────────────────────────────────────────────

  const columnDefs = useMemo((): ColDef<ProdutoManutencaoRow>[] => {
      const cols: ColDef<ProdutoManutencaoRow>[] = [
        {
          field: 'codigo',
          headerName: 'Código',
          width: isMobile ? 75 : 90,
          pinned: 'left',
          filter: 'agTextColumnFilter',
        },
        {
          field: 'descricao',
          headerName: 'Descrição',
          flex: 2,
          filter: 'agTextColumnFilter',
          minWidth: isMobile ? 100 : 220,
        },
      ];

      if (!isMobile) {
        cols.push(
          { field: 'referencia', headerName: 'Referência', width: 120, filter: 'agTextColumnFilter' },
          { field: 'codigoFabrica', headerName: 'Cód. Fábrica', width: 130, filter: 'agTextColumnFilter' },
        );
      }

      cols.push(
        { field: 'embalagem', headerName: isMobile ? 'Emb.' : 'Embalagem', width: isMobile ? 55 : 100 },
        { field: 'qtdeEmbalagem', headerName: 'Qtde', width: isMobile ? 50 : 95, filter: 'agNumberColumnFilter' },
        {
          field: 'nomeEmbalagem',
          headerName: isMobile ? 'Label' : 'Label Embalagem',
          width: isMobile ? 80 : 155,
          editable: true,
          cellStyle: { background: '#f0fdf4' },
          cellEditorParams: { useFormatter: true },
        },
        {
          field: 'visivelCat',
          headerName: isMobile ? 'Vis.' : 'Visível Catálogo',
          width: isMobile ? 50 : 130,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
          cellRenderer: (params: CustomCellRendererProps) => {
            const sim = params.data?.visivelCat !== 'N';
            return (
              <button
                onClick={() => toggleVisivelCat(params.data?.codigo)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: isMobile ? '1px 6px' : '2px 10px',
                  borderRadius: isMobile ? 8 : 12,
                  fontSize: isMobile ? 10 : 12,
                  fontWeight: 600,
                  background: sim ? '#dcfce7' : '#fee2e2',
                  color: sim ? '#15803d' : '#b91c1c',
                  border: `1px solid ${sim ? '#86efac' : '#fca5a5'}`,
                  cursor: 'pointer',
                  lineHeight: 1.3,
                }}
              >
                {isMobile ? (sim ? 'S' : 'N') : (sim ? 'Sim' : 'Não')}
              </button>
            );
          },
        },
      );

      if (!isMobile) {
        cols.push({
          field: 'dataAtualizacao',
          headerName: 'Atualização',
          width: 148,
          sortable: true,
          cellStyle: { fontSize: 12, color: '#64748b' },
          cellRenderer: (params: CustomCellRendererProps) => {
            const d = params.data?.dataAtualizacao;
            const h = params.data?.horaAtualizacao;
            if (!d) return <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>—</span>;
            const dt = new Date(d);
            const data = `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
            const hora = h ? String(h).substring(0, 5) : '';
            return <span>{data}{hora ? ` ${hora}` : ''}</span>;
          },
        });
      }

      cols.push({
        field: 'grupoId',
        headerName: isMobile ? 'Grupo' : 'Agrupamento',
        flex: 1,
        minWidth: isMobile ? 120 : 190,
        editable: false,
        cellRenderer: AgrupamentoCellRenderer,
        cellStyle: { background: '#eff6ff', padding: 0 },
      });

      return cols;
    },
    [toggleVisivelCat, isMobile],
  );

  const gridContext = useMemo(() => ({ agrupamentos, onGrupoChange }), [agrupamentos, onGrupoChange]);

  const getRowStyle = useCallback(
    (params: RowClassParams<ProdutoManutencaoRow>) => {
      if (params.data?._modificado) {
        return { background: isDark ? '#451a03' : '#fef3c7', fontWeight: '500' };
      }
      return undefined;
    },
    [isDark],
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<ProdutoManutencaoRow>) => params.data.codigo,
    [],
  );

  const qtdAlteracoes = alteracoes.size;

  return (
    <div className="flex flex-col h-full gap-2 md:gap-3">
      {/* Filtros */}
      <div className="flex flex-col gap-2 bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 px-2 py-2 md:px-3 md:py-3 shadow-sm">
        <div className="flex gap-2 items-center flex-wrap">
          <FunnelIcon className="w-4 h-4 text-zinc-400 flex-none hidden md:block" />
          <SelectField
            value={marcaSelecionada}
            onChange={setMarcaSelecionada}
            disabled={marcasCarregando}
            className="flex-1 min-w-0 md:flex-none md:w-64"
          >
            <option value="">
              {marcasCarregando ? 'Carregando...' : '— Marca —'}
            </option>
            {marcas.map(m => (
              <option key={m.codigomarca} value={m.codigomarca}>
                {m.descricaomarca}
              </option>
            ))}
          </SelectField>

          <div className="relative flex-1 min-w-0">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              placeholder={isMobile ? 'Descrição (mín. 2)' : 'Descrição do produto (mín. 2 caracteres)'}
              value={termo}
              onChange={e => setTermo(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full pl-8 pr-2 py-1.5 text-sm bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 border border-zinc-300 dark:border-zinc-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            onClick={buscar}
            disabled={!podeBuscar || carregando}
            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg shadow-sm transition-colors font-medium whitespace-nowrap"
          >
            {carregando ? '...' : 'Buscar'}
          </button>

          <button
            onClick={onCriarAgrupamento}
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-sm text-zinc-700 dark:text-zinc-200 bg-white dark:bg-zinc-700 border border-zinc-300 dark:border-zinc-600 rounded-lg shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 transition-colors font-medium whitespace-nowrap"
          >
            <PlusIcon className="w-4 h-4" />
            Novo Agrupamento
          </button>
        </div>
        {!marcaSelecionada && (
          <p className="text-[11px] md:text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <ExclamationTriangleIcon className="w-3.5 h-3.5" />
            Selecione uma marca para habilitar a busca
          </p>
        )}
      </div>

      {/* Barra: "Aplicar a todos" + Legenda */}
      <div className="flex items-center justify-between gap-2 md:gap-4 bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 px-2 py-1.5 md:px-3 md:py-2 shadow-sm flex-wrap">
        {rowData.length > 0 ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] md:text-xs text-zinc-500 dark:text-zinc-400 font-medium whitespace-nowrap">
              Aplicar a todos:
            </span>
            <SelectField
              value={grupoParaTodos}
              onChange={setGrupoParaTodos}
              className="w-40 md:w-56"
            >
              <option value="">— Sem grupo —</option>
              {agrupamentos.map(ag => (
                <option key={ag.grupoId} value={ag.grupoId}>
                  {ag.descricao}
                </option>
              ))}
            </SelectField>
            <button
              onClick={aplicarATodos}
              className="px-2.5 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors font-medium whitespace-nowrap"
            >
              Aplicar
            </button>
          </div>
        ) : (
          <div />
        )}

        <div className="hidden md:flex items-center gap-4 text-xs text-zinc-500 dark:text-zinc-400">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }} />
            Label — editável
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }} />
            Agrupamento — clique
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: '#fef3c7', border: '1px solid #fde68a' }} />
            Pendente
          </span>
        </div>
      </div>

      {/* Feedback */}
      {mensagem && (
        <div
          className={`flex items-center gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm ${
            mensagem.tipo === 'sucesso'
              ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
              : 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400'
          }`}
        >
          {mensagem.tipo === 'erro' && <ExclamationTriangleIcon className="w-4 h-4 flex-none" />}
          {mensagem.tipo === 'sucesso' && <CheckIcon className="w-4 h-4 flex-none" />}
          {mensagem.texto}
        </div>
      )}

      {/* Grid */}
      <div className="flex-1 rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700">
        <AgGridReact<ProdutoManutencaoRow>
          ref={gridRef}
          theme={isMobile ? themeMobile : themeQuartz}
          localeText={AG_GRID_LOCALE_BR}
          rowData={rowData}
          columnDefs={columnDefs}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          context={gridContext}
          onCellValueChanged={handleCellValueChanged}
          onGridReady={(e: GridReadyEvent) => e.api.sizeColumnsToFit()}
          pagination
          paginationPageSize={isMobile ? 25 : 50}
          paginationPageSizeSelector={isMobile ? [25, 50] : [25, 50, 100, 200]}
          stopEditingWhenCellsLoseFocus
          enableCellTextSelection
          overlayNoRowsTemplate='<span style="color:#94a3b8;font-size:13px">Pesquise produtos acima para começar.</span>'
          overlayLoadingTemplate='<span style="color:#94a3b8;font-size:13px">Buscando produtos...</span>'
        />
      </div>

      {/* Rodapé */}
      <div className="flex items-center justify-between gap-2 md:gap-3 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-2 md:px-4 py-1.5 md:py-2 shadow-sm flex-wrap">
        <div className="text-[11px] md:text-sm text-zinc-500 dark:text-zinc-400">
          {rowData.length > 0 ? (
            <>
              {rowData.length} produto{rowData.length !== 1 ? 's' : ''}
              {qtdAlteracoes > 0 && (
                <span className="ml-1 md:ml-2 text-amber-600 dark:text-amber-400 font-medium">
                  · {qtdAlteracoes} pendente{qtdAlteracoes !== 1 ? 's' : ''}
                </span>
              )}
            </>
          ) : (
            'Nenhum produto'
          )}
        </div>

        <div className="flex gap-2">
          {qtdAlteracoes > 0 && (
            <>
              <button
                onClick={descartar}
                disabled={salvando}
                className="flex items-center gap-1 px-2 md:px-3 py-1.5 text-[11px] md:text-xs text-zinc-600 dark:text-zinc-300 border border-zinc-300 dark:border-zinc-600 rounded-md hover:bg-zinc-50 dark:hover:bg-zinc-700 disabled:opacity-40 transition-colors"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Descartar</span>
              </button>
              <button
                onClick={salvar}
                disabled={salvando}
                className="flex items-center gap-1 px-2 md:px-3 py-1.5 text-[11px] md:text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-md transition-colors font-medium"
              >
                <CheckIcon className="w-3.5 h-3.5" />
                {salvando ? '...' : `Salvar (${qtdAlteracoes})`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
